import { MouseEvent, useEffect, useRef, useState } from 'react';
import { ChevronIcon } from '@/icons';
import { ICategoryCommission } from '@/models/commission.model';
import { InputNumber } from '@/ui';
import {
  StyledCategoryRow,
  StyledCategoryTitle,
  StyledCategoryTitleWrapper,
  StyledChevronWrapper,
  StyledCommissionInputWrap,
} from '../CommissionList/CommissionListStyles';

const MIN_COMMISSION = 0;
const MAX_COMMISSION = 100;

type TCommissionCategoryItem = {
  category: ICategoryCommission;
  level: number;
  editedValues: Record<string, number>;
  onCommissionChange: (categoryId: string, value: number | null) => void;
  canManage: boolean;
  selectedId: string | null;
  forcedOpenIds: Set<string>;
  onSelect: (categoryId: string) => void;
};

const CommissionCategoryItem = ({
  category,
  level,
  editedValues,
  onCommissionChange,
  canManage,
  selectedId,
  forcedOpenIds,
  onSelect,
}: TCommissionCategoryItem) => {
  const children = category.children ?? [];
  const canBeOpen = children.length > 0;
  const isFirstLevel = level === 0;
  const isSelected = selectedId === category.id;
  const [isOpen, setIsOpen] = useState(false);
  const isExpanded = isOpen || forcedOpenIds.has(category.id);
  const rowRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  const value = editedValues[category.id] ?? category.commission_percent;
  const isInvalid = value < MIN_COMMISSION || value > MAX_COMMISSION;

  useEffect(() => {
    if (!isSelected || hasScrolledRef.current) {
      return;
    }

    rowRef.current?.scrollIntoView({ block: 'center' });
    hasScrolledRef.current = true;
  }, [isSelected]);

  const handleRowClick = () => {
    onSelect(category.id);

    if (canBeOpen) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleCommissionClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onSelect(category.id);
  };

  return (
    <>
      <StyledCategoryRow ref={rowRef} $level={level} $isSelected={isSelected} onClick={handleRowClick}>
        <StyledCategoryTitleWrapper>
          <StyledChevronWrapper>
            {canBeOpen && (
              <ChevronIcon
                style={{
                  width: '1.25rem',
                  fill: '#000',
                  ...(isExpanded && { transform: 'rotate(180deg)' }),
                }}
              />
            )}
          </StyledChevronWrapper>
          <StyledCategoryTitle $isFirstLevel={isFirstLevel}>{category.name}</StyledCategoryTitle>
        </StyledCategoryTitleWrapper>

        <StyledCommissionInputWrap onClick={handleCommissionClick}>
          <InputNumber
            min={MIN_COMMISSION}
            max={MAX_COMMISSION}
            value={value}
            disabled={!canManage}
            status={isInvalid ? 'error' : undefined}
            onChange={(nextValue) => {
              if (typeof nextValue === 'number') {
                onCommissionChange(category.id, nextValue);
              }
            }}
            addonAfter="%"
          />
        </StyledCommissionInputWrap>
      </StyledCategoryRow>

      {isExpanded &&
        children.map((child) => (
          <CommissionCategoryItem
            key={child.id}
            category={child}
            level={level + 1}
            editedValues={editedValues}
            onCommissionChange={onCommissionChange}
            canManage={canManage}
            selectedId={selectedId}
            forcedOpenIds={forcedOpenIds}
            onSelect={onSelect}
          />
        ))}
    </>
  );
};

export default CommissionCategoryItem;
